import { PageArea, PageProps } from '..'
import UserPositionsHeader, { ColumnQuickFilterKey } from './components/Header'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  ProcessCellForExportParams,
  SortChangedEvent,
} from 'ag-grid-community'
import { usePageState } from './hooks/pageState'
import {
  POSITION_FIELD_PREFIX,
  isPositionMapColumn,
  refreshDynamicColumnDef,
  userPositionsGridOptions,
} from './gridOptions'
import { useUserPositionsData } from './hooks/userPositionsData'
import { useBulkSheetState } from './hooks/bulkSheetState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { PositionBasic, UserPositionRow } from './UserPositions'
import { DateTerm } from '../../../utils/date'
import { exportExcel } from '../../containers/BulkSheet/excel'
import { SavedUIState } from '../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { intl } from '../../../i18n'
import store from '../../../store'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../store/messages'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { dateVoService } from '../../../domain/value-object/DateVO'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'

const UserPositions = (props: PageProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [openCancel, setOpenCancel] = useState(false)
  const [openUiState, setOpenUiState] = useState(false)
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const columnSetting = useColumnSetting()
  const pageState = usePageState(props.uuid)
  const gridOptions = useMemo(() => {
    return userPositionsGridOptions()
  }, [])
  const { contextRef, updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )
  const { data, refresh, fetchPositions, save } = useUserPositionsData()
  const bulkSheetState = useBulkSheetState(
    gridApiRef.current,
    props.uuid,
    UiStateKey.UserPositions
  )
  const prevData = useRef<UserPositionRow[]>([])
  prevData.current = data
  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api
    setIsGridReady(true)
  }, [])
  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.edited)
  }, [data])

  const fetchSelectOptions = useCallback(async () => {
    const positions = await fetchPositions()
    updateContext({ positions })
  }, [fetchPositions, updateContext])

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true)
      await refresh()
      await fetchSelectOptions()
    } finally {
      setLoading(false)
    }
  }, [fetchSelectOptions, refresh])

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true)
      gridApiRef.current?.stopEditing()
      if (contextRef.current.errors?.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: contextRef.current.errors.toMessage(id => {
              const node = gridApiRef.current?.getRowNode(id)
              return !!node ? getRowNumber(node).toString() : ''
            }),
          })
        )
        return
      }
      const response = await save()
      if (!response.hasError && !response.hasWarning) {
        store.dispatch(
          addScreenMessage(props.uuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
      }
    } finally {
      setLoading(false)
    }
  }, [props.uuid, refreshAll, save])

  const onReload = useCallback(() => {
    if (hasChanged()) {
      setOpenCancel(true)
    } else {
      refreshAll()
    }
  }, [hasChanged, refreshAll])

  const onChangeDateTerm = useCallback(
    (term: DateTerm) => {
      if (!!term.startDate) {
        pageState.setStartDate(term.startDate)
      }
      if (!!term.endDate) {
        pageState.setEndDate(term.endDate)
      }
      const states = gridApiRef.current
        ?.getColumns()
        ?.filter(v => {
          if (v.isVisible()) return false
          const parent = v.getParent()
          if (!parent || !isPositionMapColumn(parent.getGroupId())) return false
          return dateVoService.isBetween(
            dateVoService.construct(v.getColId()),
            dateVoService.construct(term.startDate),
            dateVoService.construct(term.endDate),
            '[]'
          )
        })
        .map(v => ({
          colId: v.getColId(),
          hide: false,
        }))
      gridApiRef.current?.applyColumnState({ state: states })
    },
    [pageState]
  )

  const onCancelConfirm = useCallback(() => {
    setOpenCancel(false)
    refreshAll()
  }, [refreshAll])

  const onExportExcel = useCallback(() => {
    const colIds = gridApiRef.current?.getColumns() ?? []
    if (!colIds || colIds.length === 0) return
    exportExcel({
      fileNamePrefix: 'user_positions',
      gridApi: gridApiRef.current!,
      exportColIds: colIds.map(v => v.getColId()),
      getCustomExportValue: ({
        node,
        column,
        context,
        value,
      }: ProcessCellForExportParams): string | undefined => {
        if (!node || node.rowIndex === null) return undefined
        const colDef = column.getColDef()
        const { field } = colDef
        if (field?.startsWith(POSITION_FIELD_PREFIX)) {
          const positions: PositionBasic[] = context?.positions ?? []
          return positions.find(p => p.uuid === value)?.displayName ?? ''
        }
        return undefined
      },
    })
  }, [])

  const contextMenu = useCallback((params: GetContextMenuItemsParams) => {
    return []
  }, [])

  const onFirstDataRendered = useCallback(
    e => {
      bulkSheetState.onFirstDataRendered(e)
      setLoading(false)
    },
    [bulkSheetState]
  )

  const onDeletedFilterColumn = useCallback((column: ColDef) => {
    if (!column || !gridApiRef.current) return
    const filter = gridApiRef.current.getFilterModel()
    delete filter[column.colId || column.field || '']
    gridApiRef.current.setFilterModel(filter)
  }, [])

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) return
    gridApiRef.current.setFilterModel(null)
  }, [])

  const onDeleteSortedColumn = useCallback((colId: string | ColDef<any>) => {
    gridApiRef.current?.applyColumnState({
      state: [{ colId: colId.toString(), sort: null }],
    })
  }, [])

  const onDeleteSortedAllColumns = useCallback(() => {
    gridApiRef.current?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      gridApiRef.current?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    []
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filter = bulkSheetState.onFilterChanged(e)
      setFilteredColumns(filter)
    },
    [bulkSheetState]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      const sorted = bulkSheetState.onSortChanged(e)
      const columnState = gridApiRef.current?.getColumnState()
      const sortedState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedState[state.colId] = state
          }
        })
      const sortedList: SortedColumnState[] = sorted.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedState[col.colId]?.sort : null,
        }
      })
      setSortColumnsState(sortedList)
    },
    [bulkSheetState]
  )

  const onChangeColumnFilter = useCallback((value: ColumnQuickFilterKey) => {
    if (value === ColumnQuickFilterKey.INITIAL) {
      gridApiRef.current?.resetColumnState()
      gridApiRef.current?.setFilterModel(null)
      gridApiRef.current?.onFilterChanged()
    } else {
      setOpenUiState(true)
    }
  }, [])

  const callbackSelectUiState = useCallback(
    (uiState: SavedUIState) => {
      const { columnState, filterState } = uiState.UIState
      if (columnState) {
        bulkSheetState.base.saveColumnState(columnState)
        gridApiRef.current?.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
      }
      if (filterState) {
        bulkSheetState.base.saveFilterState(filterState)
        gridApiRef.current?.setFilterModel(filterState)
      }
      setOpenUiState(false)
    },
    [bulkSheetState.base]
  )

  useEffect(() => {
    if (pageState.initialized) {
      refreshAll()

      refreshDynamicColumnDef({
        api: gridApiRef.current,
        dateTerm: {
          startDate: pageState.startDate,
          endDate: pageState.endDate,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.initialized])

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  useEffect(() => {
    refreshDynamicColumnDef({
      api: gridApiRef.current,
      dateTerm: {
        startDate: pageState.startDate,
        endDate: pageState.endDate,
      },
    })
  }, [isGridReady, pageState.endDate, pageState.startDate])

  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSubmit,
        stopDefaultBehavior: true,
      },
    ],
    [data, save, pageState]
  )

  return (
    <PageArea>
      <UserPositionsHeader
        loading={loading}
        dateTerm={{
          startDate: pageState.startDate,
          endDate: pageState.endDate,
        }}
        rowHeight={pageState.rowHeight}
        onSearch={onChangeDateTerm}
        onSubmit={onSubmit}
        onReload={onReload}
        onClickExport={onExportExcel}
        onChangeRowHeight={pageState.setRowHeight}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeletedFilterColumn}
        resetFilters={resetFilters}
        sortColumnState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        columnSettingOpen={columnSetting.isOpen}
        onClickColumnSettingButton={columnSetting.toggle}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        gridOptions={gridOptions}
        getContextMenuItems={contextMenu}
        rowHeight={pageState.rowHeight}
        onRowDataUpdated={bulkSheetState.onRowDataUpdated}
        onColumnResized={bulkSheetState.rememberColumnState}
        onColumnVisible={bulkSheetState.rememberColumnState}
        onGridReady={e => {
          onGridReady(e)
          bulkSheetState.onGridReady()
        }}
        onRowGroupOpened={bulkSheetState.onRowGroupOpened}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
      />
      <Loading isLoading={loading} elem={ref.current} />
      <CancelConfirmDialog
        open={openCancel}
        onClose={() => setOpenCancel(false)}
        onConfirm={onCancelConfirm}
      />
      <SavedUIStateDialog
        applicationFunctionUuid={props.uuid}
        open={openUiState}
        title={intl.formatMessage({
          id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
        })}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        sharable={false}
        currentUIState={{
          columnState: bulkSheetState.base.columnState,
          filterState: bulkSheetState.base.filterState,
        }}
        onSelect={callbackSelectUiState}
        onClose={() => setOpenUiState(false)}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        gridApi={gridApiRef.current}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={() => setOpenUiState(true)}
        initializeColumnState={() =>
          onChangeColumnFilter(ColumnQuickFilterKey.INITIAL)
        }
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.base.columnState}
        offset={90}
      />
    </PageArea>
  )
}

export default UserPositions
